<template>
  <!--
    GlassStylePicker — the Card style switch (normal | glass) plus, in glass
    mode, where the tint comes from (the building's lit RGB lamps by default).

    Everything writes to the theme store, which applies it as data-* attributes
    on <html>; style.css maps those to the surface recipe.
  -->
  <div class="space-y-3">
    <!-- Card style -->
    <div class="flex gap-2">
      <button
        v-for="option in STYLE_OPTIONS"
        :key="option.value"
        type="button"
        class="flex-1 flex flex-col items-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-colors"
        :class="
          themeStore.cardStyle === option.value
            ? 'bg-primary-500/15 text-primary-500 ring-1 ring-primary-500/40'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
        "
        :title="tt(option.title.en, option.title.sl)"
        @click="themeStore.setCardStyle(option.value)">
        <span class="w-full px-2">
          <span
            class="block h-7 rounded-md border"
            :class="
              option.value === 'glass'
                ? 'border-white/50 shadow-[0_6px_14px_-8px_rgba(15,23,42,0.7)] backdrop-blur-sm'
                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
            "
            :style="option.value === 'glass' ? glassSwatchStyle : undefined"></span>
        </span>
        {{ tt(option.label.en, option.label.sl) }}
      </button>
    </div>

    <template v-if="themeStore.cardStyle === 'glass'">
      <!-- Tint -->
      <div>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          {{ tt('Glass tint', 'Odtenek stekla') }}
        </label>
        <div class="flex gap-2">
          <button
            v-for="tint in GLASS_TINTS"
            :key="tint.id"
            type="button"
            class="flex flex-1 items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-colors"
            :class="
              themeStore.glassTint === tint.id
                ? 'bg-primary-500/15 text-primary-500 ring-1 ring-primary-500/40'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            "
            @click="themeStore.setGlassTint(tint.id)">
            <span
              class="w-3.5 h-3.5 rounded-full ring-1 ring-black/10"
              :style="{ background: swatchFor(tint.id) }"></span>
            {{ tt(tint.label.en, tint.label.sl) }}
          </button>
        </div>
        <p v-if="themeStore.glassTint === 'lights'" class="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          <template v-if="tintStore.effective">
            {{
              tt(
                `${tintStore.effective.count} lit colour light${tintStore.effective.count === 1 ? '' : 's'} · ${tintStore.effective.hex}`,
                `${tintStore.effective.count} prižganih barvnih luči · ${tintStore.effective.hex}`,
              )
            }}
          </template>
          <template v-else>
            {{
              tt(
                'No colour light is lit — holding the last colour',
                'Nobena barvna luč ne sveti — zadržan zadnji odtenek',
              )
            }}
          </template>
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useThemeStore, GLASS_TINTS, type CardStyle, type GlassTint } from '../stores/theme';
import { useAmbientTintStore } from '../stores/ambientTint';
import type { Bilingual } from '../config/bilingual';
import { tt } from '../utils/consoleText';

const themeStore = useThemeStore();
const tintStore = useAmbientTintStore();

const STYLE_OPTIONS: Array<{ value: CardStyle; label: Bilingual; title: Bilingual }> = [
  {
    value: 'glass',
    label: { en: 'Glass', sl: 'Steklo' },
    title: { en: 'Tinted, blurred panels with tinted shadows', sl: 'Obarvane, zamegljene plošče z obarvanimi sencami' },
  },
  {
    value: 'normal',
    label: { en: 'Normal', sl: 'Navadno' },
    title: {
      en: 'Plain panels — no tint and no blur, just a solid card and its edge',
      sl: 'Navadne plošče — brez odtenka in zameglitve, le polna plošča z obrobo',
    },
  },
];

const ACCENT_HEX = '#1fbe37';
const NEUTRAL_HEX = '#64748b';

function swatchFor(tint: GlassTint): string {
  if (tint === 'accent') return ACCENT_HEX;
  if (tint === 'neutral') return NEUTRAL_HEX;
  return tintStore.effective?.hex ?? tintStore.lastMix?.hex ?? NEUTRAL_HEX;
}

/** The style buttons' preview: the same tinted glass the cards will wear. */
const glassSwatchStyle = computed(() => {
  const tint = swatchFor(themeStore.glassTint);
  return {
    background: `linear-gradient(155deg, color-mix(in srgb, ${tint} 45%, rgba(255,255,255,0.55)), color-mix(in srgb, ${tint} 12%, rgba(255,255,255,0.35)))`,
  };
});
</script>
